'use client';

import { ReactNode, useEffect, useMemo } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { AccessLevelUtils } from '@ravago/shared/page-data/utils/access-level';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';

import { redirect, usePathname } from 'next/navigation';

export default abstract class AuthGuardService {
   static isAuth = (component: ReactNode, redirectUser: boolean, locale?: string, allowedGroups?: UserGroups[]): ReactNode | null => {
      const { loading, user, login, canAuthenticate } = useAuthentication();
      const pathName = usePathname();

      const userHasAccess = useMemo(() => AccessLevelUtils.canAccess(user, allowedGroups, redirectUser), [allowedGroups, user]);

      useEffect(() => {
         if (!loading && !userHasAccess && redirectUser) {
            if (user) {
               redirect(`/`);
            } else {
               // Check if we're dealing with an excludedCountry before triggering the login.
               if (!canAuthenticate) redirect(`/${locale}`);

               login(pathName);
            }
         }
      }, [loading, userHasAccess, pathName, redirectUser, locale, canAuthenticate]);

      if (!userHasAccess) {
         if (loading && redirectUser) {
            return (
               <Box spacing="lg" width="100%" direction="column" items="center">
                  <Spinner />
               </Box>
            );
         }

         return null;
      }

      return component;
   };
}
